import { useEffect } from "react";
import { logException } from "@wss/error-tracking";
import { getErrorMessage } from "@wss/error-tracking/utils";
import { createGlobalStore } from "@wss/store";
import { IsEmptyGuid } from "~/common/helpers/constants";
import { PlusSubscription } from "~/my-account/Plus/plusSubscription";
import { plusSubscriptionProvider } from "~/my-account/Plus/plusSubscriptionProvider";

type Complete = { status: "complete"; subscriptions: PlusSubscription[] };
type Loading = { status: "loading" };
type Idle = { status: "idle" };
type Error = { status: "error"; error: string };
type PlusSubscriptionStatus = Complete | Loading | Idle | Error;

export const plusSubscriptionStore = createGlobalStore<PlusSubscriptionStatus>(
  () => ({
    status: "idle",
  }),
);

const requestPlusSubscriptions = async (guid: string): Promise<void> => {
  if (!guid || IsEmptyGuid(guid)) {
    return;
  }
  plusSubscriptionStore.setState({ status: "loading" });
  try {
    const provider = plusSubscriptionProvider();
    const { result } = await provider.getSubscriptions(guid);
    if (result?.subscriptions) {
      plusSubscriptionStore.setState({
        status: "complete",
        subscriptions: result.subscriptions,
      });
    } else {
      plusSubscriptionStore.setState({
        status: "error",
        error: "Unexpected error occurred getting plus subscriptions.",
      });
    }
  } catch (error) {
    plusSubscriptionStore.setState({
      status: "error",
      error: getErrorMessage(error),
    });
    logException(error);
  }
};

/**
 * Custom React hook for fetching Plus Subscriptions. Creates a global state to
 * prevent duplicated calls to fetch subscription data.
 *
 * @param guid Unique identifier of user
 * @returns {{ subscriptions: PlusSubscription[] }} A list of plus subscriptions tied to that guid.
 */
const usePlusSubscriptions = (guid: string) => {
  useEffect(() => {
    if (plusSubscriptionStore.getState().status === "idle") {
      requestPlusSubscriptions(guid);
    }
  }, [guid]);

  return plusSubscriptionStore.useState();
};

export default usePlusSubscriptions;
